import React, { useState } from 'react';
import { Portfolio, InvestmentFolio, InvestmentForm, InvestmentType, FamilyMember } from '../types';
import { 
  addInvestment, 
  updateInvestment, 
  deleteInvestment,
  transferInvestment 
} from '../utils/apiStorage';
import { 
  formatCurrency, 
  formatPercentage, 
  validateInvestmentForm,
  getInvestmentTypeColor 
} from '../utils/calculations';
import { Plus, Edit, Trash2, ArrowRightLeft, Search, Briefcase, Upload, X, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import apiService from '../services/api';
import { CASUploadResponse } from '../types';

interface InvestmentsProps {
  portfolio: Portfolio;
  setPortfolio: (portfolio: Portfolio) => void;
  hideHeader?: boolean;
}

const Investments: React.FC<InvestmentsProps> = ({ portfolio, setPortfolio, hideHeader = false }) => {
  const [showForm, setShowForm] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showCasModal, setShowCasModal] = useState(false);
  const [editingInvestment, setEditingInvestment] = useState<InvestmentFolio | null>(null);
  const [transferringInvestment, setTransferringInvestment] = useState<InvestmentFolio | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<InvestmentType | 'All'>('All');
  const [filterMember, setFilterMember] = useState<string>('All');
  
  // CAS Upload modal state
  const [casFile, setCasFile] = useState<File | null>(null);
  const [casPassword, setCasPassword] = useState<string>('');
  const [casFamilyMemberId, setCasFamilyMemberId] = useState<string>('');
  const [casUploading, setCasUploading] = useState<boolean>(false);
  const [casUploadResult, setCasUploadResult] = useState<CASUploadResponse | null>(null);
  
  const [formData, setFormData] = useState<InvestmentForm>({
    familyMemberId: '',
    type: 'Mutual Funds',
    name: '',
    description: '',
    investedAmount: '',
    currentValue: '',
    units: '',
    purchaseDate: '',
    maturityDate: '',
    interestRate: '',
  });
  const [errors, setErrors] = useState<string[]>([]);

  const investmentTypes: InvestmentType[] = [
    'Mutual Funds',
    'SGB',
    'Digital Gold',
    'Indian Stocks',
    'US Stocks',
    'Corporate Bonds',
    'Government Bonds',
    'Fixed Deposits (FD)',
    'Bank Savings Account',
    'Properties',
    'Pension Fund (PF)',
    'National Pension Scheme (NPS)',
    'Sukanya Samriddhi Yojana (SSY)',
  ];

  // Helper function to get family member ID from investment (handles both string and object)
  const getFamilyMemberId = (investment: InvestmentFolio): string => {
    if (typeof investment.familyMemberId === 'string') {
      return investment.familyMemberId;
    }
    if (typeof investment.familyMemberId === 'object' && investment.familyMemberId !== null) {
      const member = investment.familyMemberId as FamilyMember;
      return member._id || member.id || '';
    }
    return '';
  };

  // Helper function to normalize ID for comparison (handles string conversion)
  const normalizeId = (id: string | undefined | null): string => {
    if (!id) return '';
    return String(id);
  };

  // Filter investments
  const filteredInvestments = portfolio.investments.filter(investment => {
    const matchesSearch = investment.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         investment.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'All' || investment.type === filterType;
    
    // Normalize family member ID comparison
    const investmentMemberId = normalizeId(getFamilyMemberId(investment));
    const filterMemberId = normalizeId(filterMember);
    const matchesMember = filterMember === 'All' || investmentMemberId === filterMemberId;
    
    return matchesSearch && matchesType && matchesMember;
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validationErrors = validateInvestmentForm(formData);
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      if (editingInvestment) {
        // Update existing investment
        const updatedInvestment: InvestmentFolio = {
          ...editingInvestment,
          familyMemberId: formData.familyMemberId,
          type: formData.type,
          name: formData.name.trim(),
          description: formData.description?.trim() || undefined,
          investedAmount: parseFloat(formData.investedAmount),
          currentValue: parseFloat(formData.currentValue),
          units: formData.units ? parseFloat(formData.units) : undefined,
          purchaseDate: formData.purchaseDate,
          maturityDate: formData.maturityDate || undefined,
          interestRate: formData.interestRate ? parseFloat(formData.interestRate) : undefined,
        };
        
        const updatedPortfolio = await updateInvestment(portfolio, updatedInvestment);
        setPortfolio(updatedPortfolio);
      } else {
        // Add new investment
        const newInvestment: InvestmentFolio = {
          familyMemberId: formData.familyMemberId,
          type: formData.type,
          name: formData.name.trim(),
          description: formData.description?.trim() || undefined,
          investedAmount: parseFloat(formData.investedAmount),
          currentValue: parseFloat(formData.currentValue),
          units: formData.units ? parseFloat(formData.units) : undefined,
          purchaseDate: formData.purchaseDate,
          maturityDate: formData.maturityDate || undefined,
          interestRate: formData.interestRate ? parseFloat(formData.interestRate) : undefined,
        };
        
        const updatedPortfolio = await addInvestment(portfolio, newInvestment);
        setPortfolio(updatedPortfolio);
      }

      resetForm();
    } catch (error) {
      console.error('Error saving investment:', error);
      setErrors(['Failed to save investment. Please try again.']);
    }
  };

  const handleEdit = (investment: InvestmentFolio) => {
    setEditingInvestment(investment);
    const familyMemberId = typeof investment.familyMemberId === 'string' 
      ? investment.familyMemberId 
      : (investment.familyMemberId as any)?._id || (investment.familyMemberId as any)?.id || '';
    
    setFormData({
      familyMemberId,
      type: investment.type,
      name: investment.name,
      description: investment.description || '',
      investedAmount: investment.investedAmount.toString(),
      currentValue: investment.currentValue.toString(),
      units: investment.units?.toString() || '',
      purchaseDate: investment.purchaseDate,
      maturityDate: investment.maturityDate || '',
      interestRate: investment.interestRate?.toString() || '',
    });
    setShowForm(true);
    setErrors([]);
  };

  const handleDelete = async (investment: InvestmentFolio) => {
    if (window.confirm(`Are you sure you want to delete "${investment.name}"?`)) {
      try {
        const investmentId = investment._id || investment.id || '';
        const updatedPortfolio = await deleteInvestment(portfolio, investmentId);
        setPortfolio(updatedPortfolio);
      } catch (error) {
        console.error('Error deleting investment:', error);
        alert('Failed to delete investment. Please try again.');
      }
    }
  };

  const handleTransfer = (investment: InvestmentFolio) => {
    setTransferringInvestment(investment);
    setShowTransferModal(true);
  };

  const handleCasFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.type === 'application/pdf') {
        setCasFile(file);
        setCasUploadResult(null);
      } else {
        alert('Please select a PDF file');
      }
    }
  };

  const handleCasUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!casFile || !casFamilyMemberId) {
      alert('Please select a file and family member');
      return;
    }

    setCasUploading(true);
    setCasUploadResult(null);

    try {
      const formData = new FormData();
      formData.append('casFile', casFile);
      formData.append('familyMemberId', casFamilyMemberId);
      formData.append('password', casPassword || ''); // Send empty string if no password

      const result: CASUploadResponse = await apiService.uploadCasPdf(formData);
      setCasUploadResult(result);

      if (result.success) {
        // Refresh investments data
        const investments = await apiService.getInvestments();
        setPortfolio({ ...portfolio, investments });
        
        // Close modal and reset form
        setShowCasModal(false);
        setCasFile(null);
        setCasPassword('');
        setCasFamilyMemberId('');
        
        // Reset file input
        const fileInput = document.getElementById('casFileInput') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
      }
    } catch (error) {
      console.error('CAS upload error:', error);
      setCasUploadResult({
        success: false,
        message: 'Failed to upload CAS file',
        errors: [error instanceof Error ? error.message : 'Unknown error']
      });
    } finally {
      setCasUploading(false);
    }
  };

  const closeCasModal = () => {
    setShowCasModal(false);
    setCasFile(null);
    setCasPassword('');
    setCasFamilyMemberId('');
    setCasUploadResult(null);
    const fileInput = document.getElementById('casFileInput') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };

  const executeTransfer = async (newMemberId: string) => {
    if (transferringInvestment) {
      try {
        const investmentId = transferringInvestment._id || transferringInvestment.id || '';
        const updatedPortfolio = await transferInvestment(portfolio, investmentId, newMemberId);
        setPortfolio(updatedPortfolio);
        setShowTransferModal(false);
        setTransferringInvestment(null);
      } catch (error) {
        console.error('Error transferring investment:', error);
        alert('Failed to transfer investment. Please try again.');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      familyMemberId: '',
      type: 'Mutual Funds',
      name: '',
      description: '',
      investedAmount: '',
      currentValue: '',
      units: '',
      purchaseDate: '',
      maturityDate: '',
      interestRate: '',
    });
    setShowForm(false);
    setEditingInvestment(null);
    setErrors([]);
  };

  const getFamilyMemberName = (memberIdOrObject: string | FamilyMember) => {
    if (typeof memberIdOrObject === 'string') {
      const member = portfolio.familyMembers.find(m => (m._id || m.id) === memberIdOrObject);
      return member ? member.name : 'Unknown';
    } else {
      return memberIdOrObject.name;
    }
  };

  return (
    <div className={hideHeader ? "space-y-6" : "p-6 space-y-6"}>
      {/* Header */}
      {!hideHeader && (
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center space-x-3">
              <Briefcase className="h-6 w-6 text-primary-600" />
              <h2 className="text-2xl font-bold text-gray-900">Investments</h2>
            </div>
            <p className="text-gray-600">Manage investment portfolios for all family members</p>
          </div>
        </div>
      )}
      {hideHeader && (
        <div className="flex items-center justify-end">
          <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowCasModal(true)}
            disabled={portfolio.familyMembers.length === 0}
            className="inline-flex items-center px-4 py-2 bg-white text-primary-600 border border-primary-600 rounded-lg hover:bg-primary-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Upload className="w-4 h-4 mr-2" />
            Upload CAS File
          </button>
          <button
            onClick={() => setShowForm(true)}
            disabled={portfolio.familyMembers.length === 0}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Investment
          </button>
          </div>
        </div>
      )}
      {!hideHeader && (
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowCasModal(true)}
            disabled={portfolio.familyMembers.length === 0}
            className="inline-flex items-center px-4 py-2 bg-white text-primary-600 border border-primary-600 rounded-lg hover:bg-primary-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Upload className="w-4 h-4 mr-2" />
            Upload CAS File
          </button>
          <button
            onClick={() => setShowForm(true)}
            disabled={portfolio.familyMembers.length === 0}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Investment
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search investments..."
                className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as InvestmentType | 'All')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="All">All Types</option>
              {investmentTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Family Member</label>
            <select
              value={filterMember}
              onChange={(e) => setFilterMember(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="All">All Members</option>
              {portfolio.familyMembers.map(member => (
                <option key={member._id || member.id} value={member._id || member.id}>{member.name}</option>
              ))}
            </select>
          </div>
          
          <div className="flex items-end">
            <button
              onClick={() => {
                setSearchTerm('');
                setFilterType('All');
                setFilterMember('All');
              }}
              className="w-full px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Investment Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl mx-4 my-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {editingInvestment ? 'Edit Investment' : 'Add New Investment'}
            </h3>
            
            {errors.length > 0 && (
              <div className="mb-4 p-3 bg-danger-50 border border-danger-200 rounded-lg">
                <ul className="text-sm text-danger-700 space-y-1">
                  {errors.map((error, index) => (
                    <li key={index}>• {error}</li>
                  ))}
                </ul>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Family Member *
                  </label>
                  <select
                    value={formData.familyMemberId}
                    onChange={(e) => setFormData({ ...formData, familyMemberId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">Select family member</option>
                    {portfolio.familyMembers.map(member => (
                      <option key={member._id || member.id} value={member._id || member.id}>{member.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Investment Type *
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value as InvestmentType })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    {investmentTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Investment Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="e.g., HDFC Top 100 Fund, Reliance Industries"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Optional description or notes"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Invested Amount (₹) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.investedAmount}
                    onChange={(e) => setFormData({ ...formData, investedAmount: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Current Value (₹) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.currentValue}
                    onChange={(e) => setFormData({ ...formData, currentValue: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Units/Quantity
                  </label>
                  <input
                    type="number"
                    step="0.001"
                    value={formData.units}
                    onChange={(e) => setFormData({ ...formData, units: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="0.000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Purchase Date *
                  </label>
                  <input
                    type="date"
                    value={formData.purchaseDate}
                    onChange={(e) => setFormData({ ...formData, purchaseDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Maturity Date
                  </label>
                  <input
                    type="date"
                    value={formData.maturityDate}
                    onChange={(e) => setFormData({ ...formData, maturityDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Interest Rate (%)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.interestRate}
                  onChange={(e) => setFormData({ ...formData, interestRate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="0.00"
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-primary-600 text-white py-2 px-4 rounded-lg hover:bg-primary-700 transition-colors"
                >
                  {editingInvestment ? 'Update' : 'Add'} Investment
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Transfer Modal */}
      {showTransferModal && transferringInvestment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Transfer Investment
            </h3>
            
            <p className="text-gray-600 mb-4">
              Transfer "{transferringInvestment.name}" to another family member:
            </p>

            <div className="space-y-3">
              {portfolio.familyMembers
                .filter(member => (member._id || member.id) !== transferringInvestment.familyMemberId)
                .map(member => (
                  <button
                    key={member._id || member.id}
                    onClick={() => executeTransfer(member._id || member.id || '')}
                    className="w-full p-3 text-left border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="font-medium text-gray-900">{member.name}</div>
                    <div className="text-sm text-gray-600">{member.relationship}</div>
                  </button>
                ))}
            </div>

            <div className="flex space-x-3 pt-4 mt-4 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowTransferModal(false);
                  setTransferringInvestment(null);
                }}
                className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Investments List */}
      {portfolio.familyMembers.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <Briefcase className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Family Members</h3>
          <p className="text-gray-600">
            Please add family members first before creating investments
          </p>
        </div>
      ) : filteredInvestments.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <Briefcase className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Investments Found</h3>
          <p className="text-gray-600 mb-6">
            {portfolio.investments.length === 0 
              ? "Start by adding your first investment"
              : "No investments match your current filters"
            }
          </p>
          {portfolio.investments.length === 0 && (
            <button
              onClick={() => setShowForm(true)}
              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add First Investment
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Investment
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Family Member
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Invested
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Current Value
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Growth
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredInvestments.map((investment) => {
                  const growth = investment.currentValue - investment.investedAmount;
                  const growthPercentage = investment.investedAmount > 0 
                    ? (growth / investment.investedAmount) * 100 
                    : 0;

                  return (
                    <tr key={investment.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="flex items-center">
                            <div 
                              className="w-3 h-3 rounded-full mr-3"
                              style={{ backgroundColor: getInvestmentTypeColor(investment.type) }}
                            />
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {investment.name}
                              </div>
                              <div className="text-sm text-gray-500">
                                {investment.type}
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {getFamilyMemberName(investment.familyMemberId)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(investment.investedAmount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(investment.currentValue)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm">
                          <div className={`font-medium ${
                            growth >= 0 ? 'text-success-600' : 'text-danger-600'
                          }`}>
                            {formatCurrency(growth)}
                          </div>
                          <div className={`text-xs ${
                            growthPercentage >= 0 ? 'text-success-600' : 'text-danger-600'
                          }`}>
                            {formatPercentage(growthPercentage)}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEdit(investment)}
                            className="text-primary-600 hover:text-primary-900"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleTransfer(investment)}
                            className="text-blue-600 hover:text-blue-900"
                            disabled={portfolio.familyMembers.length <= 1}
                          >
                            <ArrowRightLeft className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(investment)}
                            className="text-danger-600 hover:text-danger-900"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* CAS Upload Modal */}
      {showCasModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Upload CAS File
              </h3>
              <button
                onClick={closeCasModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCasUpload} className="space-y-4">
              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  CAS PDF File <span className="text-red-500">*</span>
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Only CDSL CAS files supported for now
                </p>
                <input
                  id="casFileInput"
                  type="file"
                  accept=".pdf"
                  onChange={handleCasFileSelect}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                  required
                />
                {casFile && (
                  <p className="mt-2 text-sm text-gray-600">
                    Selected: {casFile.name}
                  </p>
                )}
              </div>

              {/* Family Member Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Family Member <span className="text-red-500">*</span>
                </label>
                <select
                  value={casFamilyMemberId}
                  onChange={(e) => setCasFamilyMemberId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                >
                  <option value="">Select a family member</option>
                  {portfolio.familyMembers.map((member) => (
                    <option key={member._id || member.id} value={member._id || member.id}>
                      {member.name} ({member.relationship})
                    </option>
                  ))}
                </select>
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  PDF Password
                </label>
                <input
                  type="password"
                  value={casPassword}
                  onChange={(e) => setCasPassword(e.target.value)}
                  placeholder="Enter PDF password (if protected)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Leave empty if the PDF is not password protected
                </p>
              </div>

              {/* Upload Result */}
              {casUploadResult && (
                <div className={`p-4 rounded-lg ${
                  casUploadResult.success
                    ? 'bg-success-50 border border-success-200'
                    : 'bg-red-50 border border-red-200'
                }`}>
                  <div className={`flex items-center ${
                    casUploadResult.success ? 'text-success-800' : 'text-red-800'
                  }`}>
                    {casUploadResult.success ? (
                      <>
                        <CheckCircle className="w-5 h-5 mr-2" />
                        <div className="flex-1">
                          <p className="font-medium">{casUploadResult.message}</p>
                          {casUploadResult.holdingsByType && (
                            <div className="mt-2 text-sm">
                              {Object.entries(casUploadResult.holdingsByType).map(([type, count]) => (
                                <p key={type}>• {count} {type}</p>
                              ))}
                            </div>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        <AlertCircle className="w-5 h-5 mr-2" />
                        <div className="flex-1">
                          <p className="font-medium">{casUploadResult.message}</p>
                          {casUploadResult.errors && casUploadResult.errors.length > 0 && (
                            <ul className="mt-2 text-sm list-disc list-inside">
                              {casUploadResult.errors.map((error, index) => (
                                <li key={index}>{error}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={closeCasModal}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={casUploading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={casUploading || !casFile || !casFamilyMemberId}
                  className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {casUploading ? (
                    <span className="flex items-center justify-center">
                      <Loader className="w-4 h-4 mr-2 animate-spin" />
                      Uploading...
                    </span>
                  ) : (
                    'Upload & Parse'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Investments;
